type AttachmentsControllerOptions = {
  attachmentTray: HTMLElement | null;
  attachButton: HTMLButtonElement | null;
  imageUploadInput: HTMLInputElement | null;
  composeState: {
    pendingAttachments: any[];
    uploadingAttachments: any[];
  };
  getAuthToken: () => string | null;
  getSocketState: () => { readyState?: number } | null;
  connect: () => void;
  send: (payload: any) => void;
  appendError: (message: string) => void;
  formatFileSize: (value: number) => string;
  escapeHtml: (value: any) => string;
  nextLocalId: (prefix?: string) => string;
};

function replaceFileExtension(filename: string, ext: string) {
  const base = String(filename || 'image').replace(/\.[^/.]+$/, '');
  return `${base}${ext}`;
}

function loadImageFromFile(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('读取图片失败'));
    };
    img.src = url;
  });
}

export function createAttachmentsController(options: AttachmentsControllerOptions) {
  function syncAttachmentActions() {
    const uploading = options.composeState.uploadingAttachments.length > 0;
    if (options.attachButton) options.attachButton.disabled = uploading;
  }

  async function compressImageFile(file: File) {
    if (!file || !/^image\/(png|jpeg|webp)$/i.test(file.type || '')) return file;
    const img = await loadImageFromFile(file);
    const maxDimension = 2000;
    const maxOriginalBytes = 2 * 1024 * 1024;
    const largestSide = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height);
    if (file.size <= maxOriginalBytes && largestSide <= maxDimension) return file;

    const scale = Math.min(1, maxDimension / largestSide);
    const width = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const targetType = 'image/webp';
    const qualities = [0.9, 0.84, 0.78, 0.72];
    let bestBlob: Blob | null = null;
    for (const quality of qualities) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, targetType, quality));
      if (!blob) continue;
      if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
      if (blob.size <= Math.max(maxOriginalBytes, file.size * 0.72)) break;
    }
    if (!bestBlob || bestBlob.size >= file.size) return file;
    return new File([bestBlob], replaceFileExtension(file.name || 'image', '.webp'), {
      type: bestBlob.type,
      lastModified: Date.now(),
    });
  }

  function ensureAuthenticatedWs() {
    return new Promise<string>((resolve, reject) => {
      const socket = options.getSocketState();
      const authToken = options.getAuthToken();
      if (socket && socket.readyState === WebSocket.OPEN && authToken) {
        resolve(authToken);
        return;
      }
      if (!authToken) {
        reject(new Error('登录状态已失效，请重新登录后再上传图片。'));
        return;
      }
      const timeout = setTimeout(() => {
        reject(new Error('登录状态恢复超时，请重新登录后重试。'));
      }, 8000);

      const cleanup = () => {
        clearTimeout(timeout);
        document.removeEventListener('webcoding-auth-restored', onRestored);
        document.removeEventListener('webcoding-auth-failed', onFailed);
      };
      const onRestored = () => {
        cleanup();
        resolve(options.getAuthToken() || '');
      };
      const onFailed = () => {
        cleanup();
        reject(new Error('登录状态已失效，请刷新页面后重新登录再上传图片。'));
      };

      document.addEventListener('webcoding-auth-restored', onRestored);
      document.addEventListener('webcoding-auth-failed', onFailed);

      if (!socket || (socket.readyState ?? 99) > WebSocket.OPEN) {
        options.connect();
      } else if (socket.readyState === WebSocket.OPEN) {
        options.send({ type: 'auth', token: authToken });
      }
    });
  }

  async function deleteUploadedAttachment(id: string) {
    if (!id) return;
    try {
      const authToken = await ensureAuthenticatedWs();
      await fetch(`/api/attachments/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    } catch {}
  }

  function renderAttachmentLabels(attachments: any[], renderOptions: any = {}) {
    if (!Array.isArray(attachments) || attachments.length === 0) return '';
    const labels = attachments.map((attachment) => {
      const stateSuffix = attachment.storageState === 'expired' ? '（已过期）' : '';
      const name = options.escapeHtml(attachment.filename || 'image');
      return `<span class="msg-attachment-label">图片: ${name}${stateSuffix}</span>`;
    }).join('');
    return `<div class="msg-attachments${renderOptions.compact ? ' compact' : ''}">${labels}</div>`;
  }

  function renderPendingAttachments() {
    if (!options.attachmentTray) return;
    if (!options.composeState.pendingAttachments.length && !options.composeState.uploadingAttachments.length) {
      options.attachmentTray.hidden = true;
      options.attachmentTray.innerHTML = '';
      syncAttachmentActions();
      return;
    }

    options.attachmentTray.hidden = false;
    const uploadingHtml = options.composeState.uploadingAttachments.map((attachment) => `
      <div class="attachment-chip uploading">
        <div class="attachment-chip-meta">
          <span class="attachment-chip-name">${options.escapeHtml(attachment.filename || 'image')}</span>
          <span class="attachment-chip-note">上传中 · ${options.formatFileSize(attachment.size)}</span>
        </div>
      </div>
    `).join('');
    const readyHtml = options.composeState.pendingAttachments.map((attachment, index) => `
      <div class="attachment-chip" data-index="${index}">
        <div class="attachment-chip-meta">
          <span class="attachment-chip-name">${options.escapeHtml(attachment.filename || 'image')}</span>
          <span class="attachment-chip-note">${options.formatFileSize(attachment.size)} · 将随下一条消息发送</span>
        </div>
        <button class="attachment-chip-remove" type="button" data-index="${index}" title="移除">✕</button>
      </div>
    `).join('');
    const noteHtml = options.composeState.uploadingAttachments.length > 0
      ? '<div class="attachment-tray-note">图片上传中，此时发送不会包含尚未完成的图片。</div>'
      : '';

    options.attachmentTray.innerHTML = `${uploadingHtml}${readyHtml}${noteHtml}`;
    options.attachmentTray.querySelectorAll<HTMLElement>('.attachment-chip-remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = Number(btn.dataset.index);
        const [removed] = options.composeState.pendingAttachments.splice(index, 1);
        renderPendingAttachments();
        deleteUploadedAttachment(removed?.id);
      });
    });
    syncAttachmentActions();
  }

  async function uploadImageFile(file: File) {
    const authToken = await ensureAuthenticatedWs();
    const headers = {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': file.type || 'application/octet-stream',
      'X-Filename': encodeURIComponent(file.name || 'image'),
    };
    const response = await fetch('/api/attachments', {
      method: 'POST',
      headers,
      body: file,
    });
    const rawText = await response.text();
    let data = null;
    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch {
      data = null;
    }
    if (response.status === 401) {
      throw new Error('登录状态已失效，请刷新页面后重新登录再上传图片。');
    }
    if (response.status === 413) {
      throw new Error('图片大小超过当前上传限制，请压缩到 10MB 以内后重试。');
    }
    if (!response.ok || !data?.ok) {
      throw new Error(data?.message || `上传失败 (${response.status})`);
    }
    return data.attachment;
  }

  async function handleSelectedImageFiles(fileList: FileList | File[] | null | undefined) {
    const files = Array.from(fileList || []).filter((file) => file && /^image\//.test(file.type || '')) as File[];
    if (!files.length) return;
    const totalAttachments = options.composeState.pendingAttachments.length + options.composeState.uploadingAttachments.length + files.length;
    if (totalAttachments > 4) {
      options.appendError('单条消息最多附带 4 张图片。');
      return;
    }
    const batch = files.map((file, index) => ({
      id: options.nextLocalId(`upload-${index}`),
      filename: file.name || 'image',
      size: file.size || 0,
    }));
    options.composeState.uploadingAttachments.push(...batch);
    renderPendingAttachments();
    try {
      const results = await Promise.allSettled(files.map(async (file) => {
        const optimized = await compressImageFile(file);
        return uploadImageFile(optimized);
      }));
      const errors: string[] = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          options.composeState.pendingAttachments.push(result.value);
        } else {
          errors.push((result.reason as any)?.message || '图片上传失败');
        }
      }
      if (errors.length > 0) options.appendError(errors[0]);
    } catch (err: any) {
      options.appendError(err.message || '图片上传失败');
    } finally {
      options.composeState.uploadingAttachments = options.composeState.uploadingAttachments.filter(
        (item) => !batch.some((entry) => entry.id === item.id),
      );
      renderPendingAttachments();
      if (options.imageUploadInput) options.imageUploadInput.value = '';
    }
  }

  return {
    renderAttachmentLabels,
    renderPendingAttachments,
    handleSelectedImageFiles,
  };
}
